import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import api from '../utils/api';
import { useQuery } from '../utils/query';
import { useForm } from '../utils/form';

import { routes } from '../routes';
import { can } from '../can';

import { Loading } from './PageStatus';
import PageHeader from '../components/common/PageHeader';
import Input from '../components/common/Input';
import Toggle from '../components/common/Toggle';
import Select, { indexToOptions } from '../components/common/Select';
import Pagination from '../components/common/Pagination';
import Button from '../components/common/Button';
import Modal from '../components/common/Modal';
import { List, Header, Body, Row, Cell } from '../components/common/List';

import css from './Product.css';

const productFields = {
	sku: ['text', true],
	sku2: ['text'],
	name: ['text', true],
	clientId: ['number'],
	preferences: ['json'],
};

const productPreferencesFields = {
	color: ['text'],
	size: ['text'],
	weight: ['number'],
	enabled: ['boolean'],
};

const defaultPreferences = {
	color: '',
	size: null,
	weight: null,
	enabled: false,
};

const sizeOptions = [
	{ value: 'small', label: 'Small 5x5x5' },
	{ value: 'medium', label: 'Medium 15x15x15' },
	{ value: 'large', label: 'Large 30x30x30' },
];

const toFieldLabel = (fieldName) => {
	if (fieldName === 'enabled') return 'Habilitado';
	if (fieldName === 'weight') return 'Peso';
	if (fieldName === 'size') return 'Talla';
	if (fieldName === 'color') return 'Color';
	return fieldName;
};

const Products = (props) => {
	const query = useQuery(props.location, props.history);
	const { user } = api.auth();
	const { index, actions } = api.products.index(query.obj);
	const { index: reservedOrdersIndex, actions: reservedOrdersActions } = api.orders.index(null, { lazy: true });
	const { item: productNewItem, actions: productNewActions } = api.products.newItem();
	const { actions: clientIndexActions } = api.clients.index(null, { lazy: true });
	const [modalOpen, setModalOpen] = useState(false);
	const [viewModalOpen, setViewModalOpen] = useState(false);
	const [editModalOpen, setEditModalOpen] = useState(false);
	const [selectedProduct, setSelectedProduct] = useState(null);
	const [editingProduct, setEditingProduct] = useState(null);
	const [productPreferences, setProductPreferences] = useState(defaultPreferences);
	const [editProductPreferences, setEditProductPreferences] = useState(defaultPreferences);
	const [gatheredOrders, setGatheredOrders] = useState([]);
	const form = useForm(productFields, productNewItem.data);
	const editForm = useForm(productFields, editingProduct);
	const { item: productEditItem, actions: productEditActions } = api.products.item(
		editingProduct ? editingProduct.id : null,
		null,
		{ lazy: true }
	);
	const isAdmin = user.role == 'admin';

	const handleCheckStock = async () => {
		const params = { status: 'reserved,ready', clientId: 106 };
		const fetchedOrders = await reservedOrdersActions.getAll(params);
		const collectedOrders = fetchedOrders && fetchedOrders.toJS ? fetchedOrders.toJS() : [];
		setGatheredOrders(collectedOrders);
		console.log('Gathered orders from Check stock:', collectedOrders);
	};

	useEffect(() => {
		handleCheckStock();
	}, []);

	const gatheredOrderProductIds = useMemo(() => {
		const ids = new Set();

		gatheredOrders.forEach((order) => {
			const extras = order && order.extras;
			const parsedExtras = typeof extras === 'string'
				? (() => {
					try {
						return JSON.parse(extras);
					} catch (e) {
						return {};
					}
				})()
				: extras || {};

			const orderProducts = Array.isArray(parsedExtras.products) ? parsedExtras.products : [];
			orderProducts.forEach((product) => {
				if (product && product.id != null) ids.add(String(product.id));
			});
		});

		return ids;
	}, [gatheredOrders]);

	const getClients = async (search) => {
		const clientIndex = await clientIndexActions.get({ search, perPage: 50 });
		return indexToOptions(clientIndex, { value: 'id', label: 'name' });
	};

	const onClientChange = (name, value) => {
		form.setValue('clientId', value);
	};

	const onEditClientChange = (name, value) => {
		editForm.setValue('clientId', value);
	};

	const onPreferenceInputChange = (name, value) => {
		setProductPreferences((current) => ({
			...current,
			[name]: value,
		}));
	};

	const onEditPreferenceInputChange = (name, value) => {
		setEditProductPreferences((current) => ({
			...current,
			[name]: value,
		}));
	};

	const buildPreferences = (prefs) => {
		return Object.entries(prefs).reduce((acc, [key, value]) => {
			const fieldType = productPreferencesFields[key] && productPreferencesFields[key][0];
			if (fieldType === 'number') {
				if (value !== '' && value != null) acc[key] = Number(value);
				return acc;
			}
			if (fieldType === 'boolean') {
				if (value === true) acc[key] = true;
				return acc;
			}
			if (value !== '' && value != null) acc[key] = value;
			return acc;
		}, {});
	};

	const resetForm = () => {
		form.setValue('sku', null, true);
		form.setValue('sku2', null, true);
		form.setValue('name', null, true);
		form.setValue('clientId', null, true);
		form.setValue('preferences', null, true);
		setProductPreferences(defaultPreferences);
	};

	const openModal = () => setModalOpen(true);
	const closeModal = () => {
		setModalOpen(false);
		resetForm();
	};

	const openViewModal = (product) => {
		setSelectedProduct(product);
		setViewModalOpen(true);
	};

	const closeViewModal = () => {
		setViewModalOpen(false);
		setSelectedProduct(null);
	};

	const closeEditModal = () => {
		setEditModalOpen(false);
		setEditingProduct(null);
		setEditProductPreferences(defaultPreferences);
	};

	const openEditFromDetails = () => {
		if (!selectedProduct) return;
		setEditingProduct(selectedProduct);
		setEditProductPreferences({
			...defaultPreferences,
			...normalizePreferences(selectedProduct.preferences),
		});
		setViewModalOpen(false);
		setEditModalOpen(true);
	};

	const normalizePreferences = (preferences) => {
		if (!preferences) return {};
		if (typeof preferences === 'string') {
			try {
				return JSON.parse(preferences);
			} catch (e) {
				return {};
			}
		}
		return preferences;
	};

	const getPreferencePills = (preferences) => {
		const normalized = normalizePreferences(preferences);
		const enabled = normalized.enabled === true;
		const pills = Object.entries(normalized)
			.filter(([key, value]) => key !== 'enabled' && value !== '' && value != null && value !== false)
			.map(([key, value]) => {
				const label = toFieldLabel(key);
				if (value === true) return label;
				return `${label}: ${value}`;
			});

		return { enabled, pills };
	};

	const handleCreate = async () => {
		if (!form.validate(null, true)) return;

		let values = form.getValues();
		if (!isAdmin && user.clientId) values = values.set('clientId', user.clientId);
		const builtPreferences = buildPreferences(productPreferences);
		if (Object.keys(builtPreferences).length > 0) {
			values = values.set('preferences', builtPreferences);
		}

		const created = await productNewActions.create(values);
		if (created) {
			closeModal();
			await actions.get();
		}
	};

	const handleUpdate = async () => {
		if (!editingProduct) return;
		if (!editForm.validate(null, true)) return;

		let values = editForm.getValues();
		if (!isAdmin && user.clientId) values = values.set('clientId', user.clientId);

		const builtPreferences = buildPreferences(editProductPreferences);
		values = values.set('preferences', builtPreferences);

		const updated = await productEditActions.update(values, null, null, editingProduct.id);
		if (updated) {
			closeEditModal();
			await actions.get();
		}
	};

	const handleDelete = async () => {
		if (!selectedProduct) return;

		const deleted = await productEditActions.delete(null, null, selectedProduct.id);
		if (deleted) {
			closeViewModal();
			await actions.get();
		}
	};

	return (
		<main>
			<PageHeader title="Productos" {...index}>
				{can(user, 'create', 'products') && (
					<Button noMargin onClick={openModal}>
						Nuevo
					</Button>
				)}
			</PageHeader>

			<Modal open={modalOpen} onClose={closeModal}>
				<div>
					<h3>Nuevo producto</h3>
					{isAdmin && (
						<Select
							label="Cliente"
							name="clients"
							onChange={onClientChange}
							loadOptions={getClients}
							form={form}
							noOptionsMessage={(a) => {
								if (a.inputValue) {
									return `No hay clientes que contengan "${a.inputValue}"`;
								}
								return 'No hay clientes.';
							}}
						/>
					)}
					<Input name="sku" label="SKU" placeholder="SKU-001" form={form} onEnter={handleCreate} />
					<Input name="sku2" label="SKU 2" placeholder="SKU-ALT-001" form={form} onEnter={handleCreate} />
					<Input name="name" label="Nombre" placeholder="Nombre de producto" form={form} onEnter={handleCreate} />

					<hr />
					<h4>Preferencias del producto</h4>
					{Object.entries(productPreferencesFields).map(([fieldName, [fieldType]]) => {
						if (fieldName === 'size') {
							const sizeValue = sizeOptions.find((o) => o.value === productPreferences.size)?.value || null;
							return (
								<Select
									key={`preference-select-${fieldName}`}
									name={fieldName}
									label={toFieldLabel(fieldName)}
									options={sizeOptions}
									value={sizeValue}
									onChange={(name, value) => onPreferenceInputChange(name, value)}
								/>
							);
						}

						if (fieldType === 'boolean') {
							return (
								<Toggle
									key={`preference-toggle-${fieldName}`}
									name={fieldName}
									label={toFieldLabel(fieldName)}
									value={!!productPreferences[fieldName]}
									onChange={onPreferenceInputChange}
								/>
							);
						}

						return (
							<Input
								key={`preference-input-${fieldName}`}
								name={fieldName}
								label={toFieldLabel(fieldName)}
								type={fieldType}
								value={productPreferences[fieldName] ?? ''}
								onChange={onPreferenceInputChange}
								onEnter={handleCreate}
							/>
						);
					})}
					<pre>{JSON.stringify(buildPreferences(productPreferences), null, 2)}</pre>
					{productNewItem.error && <p>{productNewItem.error}</p>}
					<Button onClick={handleCreate} loading={productNewItem.loading}>
						Crear
					</Button>
				</div>
			</Modal>

			<Modal open={viewModalOpen} onClose={closeViewModal}>
				<div>
					<h3>Detalle de producto</h3>
					{selectedProduct ? (
						<>
							<table className={css.detailsTable}>
								<tbody>
								<tr>
									<td className={css.detailsLabelCell}>Nombre</td>
									<td className={css.detailsValueCell}>{selectedProduct.name || '-'}</td>
								</tr>
								<tr>
									<td className={css.detailsLabelCell}>ID</td>
									<td className={css.detailsValueCell}>{selectedProduct.id}</td>
								</tr>
								<tr>
									<td className={css.detailsLabelCell}>SKU</td>
									<td className={css.detailsValueCell}>{selectedProduct.sku || '-'}</td>
								</tr>
								<tr>
									<td className={css.detailsLabelCell}>SKU 2</td>
									<td className={css.detailsValueCell}>{selectedProduct.sku2 || '-'}</td>
								</tr>
								<tr>
									<td className={css.detailsLabelCell}>Cliente ID</td>
									<td className={css.detailsValueCell}>{selectedProduct.clientId || '-'}</td>
								</tr>
								<tr>
									<td className={`${css.detailsLabelCell} ${css.detailsTopCell}`}>Preferencias</td>
									<td className={css.detailsValueCell}>
										<pre className={css.detailsPre}>{JSON.stringify(normalizePreferences(selectedProduct.preferences), null, 2)}</pre>
									</td>
								</tr>
								</tbody>
							</table>
							<div className={css.detailsActions}>
								<Button onClick={openEditFromDetails}>Editar</Button>
								{can(user, 'delete', 'products') && (
									<Button
										destructive
										confirm="¿Eliminar este Producto?"
										onClick={handleDelete}
										loading={productEditItem.loading == 'delete'}>
										Eliminar
									</Button>
								)}
							</div>
							{productEditItem.error && <p>{productEditItem.error}</p>}
						</>
					) : null}
				</div>
			</Modal>

			<Modal open={editModalOpen} onClose={closeEditModal}>
				<div>
					<h3>Editar producto</h3>
					{isAdmin && (
						<Select
							label="Cliente"
							name="clients"
							onChange={onEditClientChange}
							loadOptions={getClients}
							form={editForm}
							noOptionsMessage={(a) => {
								if (a.inputValue) {
									return `No hay clientes que contengan "${a.inputValue}"`;
								}
								return 'No hay clientes.';
							}}
						/>
					)}
					<Input name="sku" label="SKU" placeholder="SKU-001" form={editForm} onEnter={handleUpdate} />
					<Input name="sku2" label="SKU 2" placeholder="SKU-ALT-001" form={editForm} onEnter={handleUpdate} />
					<Input name="name" label="Nombre" placeholder="Nombre de producto" form={editForm} onEnter={handleUpdate} />

					<hr />
					<h4>Preferencias del producto</h4>
					{Object.entries(productPreferencesFields).map(([fieldName, [fieldType]]) => {
						if (fieldName === 'size') {
							const sizeValue = sizeOptions.find((o) => o.value === editProductPreferences.size)?.value || null;
							return (
								<Select
									key={`edit-preference-select-${fieldName}`}
									name={fieldName}
									label={toFieldLabel(fieldName)}
									options={sizeOptions}
									value={sizeValue}
									onChange={(name, value) => onEditPreferenceInputChange(name, value)}
								/>
							);
						}

						if (fieldType === 'boolean') {
							return (
								<Toggle
									key={`edit-preference-toggle-${fieldName}`}
									name={fieldName}
									label={toFieldLabel(fieldName)}
									value={!!editProductPreferences[fieldName]}
									onChange={onEditPreferenceInputChange}
								/>
							);
						}

						return (
							<Input
								key={`edit-preference-input-${fieldName}`}
								name={fieldName}
								label={toFieldLabel(fieldName)}
								type={fieldType}
								value={editProductPreferences[fieldName] ?? ''}
								onChange={onEditPreferenceInputChange}
								onEnter={handleUpdate}
							/>
						);
					})}
					{productEditItem.error && <p>{productEditItem.error}</p>}
					<Button onClick={handleUpdate} loading={productEditItem.loading}>
						Actualizar
					</Button>
				</div>
			</Modal>

			<div className={css.searchControls}>
				<Button
					small
					noMargin
					className={css.checkStockButton}
					onClick={handleCheckStock}
					loading={!!reservedOrdersIndex.loading}>
					Check stock
				</Button>
				<Input
					className={css.searchInputCompact}
					label="Buscar por SKU o nombre:"
					name="search"
					onChange={query.set}
					value={query.get('search')}
					placeholder="SKU o nombre"
				/>
			</div>

			{index.loaded ? (
				<List>
					<Header>
						<Cell head>ID</Cell>
						<Cell head>Nombre</Cell>
						<Cell head>SKU</Cell>
						<Cell head>Preferencias</Cell>
						<Cell head>Cliente ID</Cell>
					</Header>
					<Body>
						{index.data.toJS().map((product, i) => {
							const to = routes('products');
							const { enabled, pills } = getPreferencePills(product.preferences);
							const isOutOfStock = gatheredOrderProductIds.has(String(product.id));
							return (
								<Row key={`product-row-${i}`}>
									<Cell>
										<Link
											to={to}
											onClick={(e) => {
												e.preventDefault();
												openViewModal(product);
											}}>
											{product.id}
										</Link>
									</Cell>
									<Cell>
										<Link
											to={to}
											onClick={(e) => {
												e.preventDefault();
												openViewModal(product);
											}}>
											<div className={css.nameCellContent}>
												<span>{product.name}</span>
												{isOutOfStock && (
													<span className={css.stockLabelsRow}>
														<span className={css.inCirculationPill}>En circulacion</span>
														<span className={css.outOfStockGlow}>Fuera de stock</span>
													</span>
												)}
												<span className={`${css.preferenceSwitch} ${css.preferenceSwitchLarge} ${enabled ? css.preferenceSwitchEnabled : css.preferenceSwitchDisabled}`}>
													{enabled ? 'Habilitado' : 'Deshabilitado'}
												</span>
											</div>
										</Link>
									</Cell>
									<Cell>
										<Link to={to}>{product.sku}</Link>
									</Cell>
										<Cell>
											<Link to={to}>
												<div className={css.preferencePills}>
													{pills.length > 0 ? (
														pills.map((pill, pillIndex) => (
															<span key={`product-pref-${product.id}-${pillIndex}`} className={css.preferencePill}>
																{pill}
															</span>
														))
													) : (
														<span className={css.preferencePillEmpty}>-</span>
													)}
												</div>
											</Link>
										</Cell>
										<Cell>
											<Link to={to}>{product.clientId || '-'}</Link>
										</Cell>
								</Row>
							);
						})}
					</Body>
				</List>
			) : (
				<Loading compact />
			)}

			{index.pageInfo && <Pagination {...index.pageInfo.toJS()} onChange={query.setPage} />}
		</main>
	);
};

export default Products;
